import { supabase } from '../config/supabase';
import { formatCurrency } from '../utils/currency';

export interface Contact {
  id: string;
  business_id: string;
  contact_id: string; // Business-specific identifier
  name: string;
  type: 'customer' | 'vendor' | 'employee' | 'other';
  email?: string;
  phone?: string;
  address?: string;
  profile_picture_url?: string; // URL to profile picture
  balance: number;
  is_editable: boolean;
  created_at: string;
  updated_at: string;
}

export interface ContactFormData {
  contact_id: string; // Now mandatory
  name: string;
  type: 'customer' | 'vendor' | 'employee' | 'other';
  email?: string;
  phone?: string;
  address?: string;
  profile_picture_url?: string;
}

export const contactsService = {
  // Generate a unique contact ID for the business
  async generateContactId(businessId?: string): Promise<string> {
    try {
      if (!businessId) {
        throw new Error('Business ID is required to generate contact ID');
      }

      // Get the count of existing contacts for this business
      const { count, error: countError } = await supabase
        .from('contacts')
        .select('*', { count: 'exact', head: true })
        .eq('business_id', businessId);

      if (countError) {
        console.error('Error counting contacts:', countError);
        // Don't throw here, just use 0 as fallback
      }

      // Generate contact ID: C-{timestamp}-{count+1}
      const timestamp = Date.now();
      const sequence = (count || 0) + 1;
      const contactId = `C-${timestamp}-${sequence}`;

      console.log('Generated contact ID:', contactId);
      return contactId;

    } catch (error) {
      console.error('Error generating contact ID:', error);
      // Return a fallback ID instead of throwing
      const fallbackId = `C-${Date.now()}-${Math.floor(Math.random() * 1000)}`;
      console.log('Using fallback contact ID:', fallbackId);
      return fallbackId;
    }
  },

  // Get all contacts for the business
  async getContacts(businessId?: string): Promise<{ contacts: Contact[]; error: Error | null }> {
    try {
      let query = supabase
        .from('contacts')
        .select('*')
        .order('name', { ascending: true });

      // Filter by business_id if provided
      if (businessId) {
        query = query.eq('business_id', businessId);
      }

      const { data, error } = await query;

      if (error) throw error;
      return { contacts: data || [], error: null };
    } catch (error) {
      return { contacts: [], error: error as Error };
    }
  },

  // Get contact by ID
  async getContactById(id: string, businessId?: string): Promise<{ contact: Contact | null; error: Error | null }> {
    try {
      let query = supabase
        .from('contacts')
        .select('*')
        .eq('id', id);

      // Filter by business_id if provided for security
      if (businessId) {
        query = query.eq('business_id', businessId);
      }

      const { data, error } = await query.single();

      if (error) throw error;
      return { contact: data, error: null };
    } catch (error) {
      return { contact: null, error: error as Error };
    }
  },

  // Create a new contact
  async createContact(contactData: ContactFormData, businessId?: string): Promise<{ contact: Contact | null; error: Error | null }> {
    try {
      if (!businessId) {
        throw new Error('Business ID is required');
      }

      const { data, error } = await supabase
        .from('contacts')
        .insert([
          {
            business_id: businessId,
            contact_id: contactData.contact_id,
            name: contactData.name,
            type: contactData.type,
            email: contactData.email,
            phone: contactData.phone,
            address: contactData.address,
            profile_picture_url: contactData.profile_picture_url,
            balance: 0.00,
            is_editable: true,
          },
        ])
        .single();

      if (error) throw error;
      return { contact: data, error: null };
    } catch (error) {
      return { contact: null, error: error as Error };
    }
  },

  // Update an existing contact
  async updateContact(id: string, contactData: ContactFormData): Promise<{ contact: Contact | null; error: Error | null }> {
    try {
      const { data, error } = await supabase
        .from('contacts')
        .update({
          contact_id: contactData.contact_id,
          name: contactData.name,
          type: contactData.type,
          email: contactData.email,
          phone: contactData.phone,
          address: contactData.address,
          profile_picture_url: contactData.profile_picture_url,
        })
        .eq('id', id)
        .single();

      if (error) throw error;
      return { contact: data, error: null };
    } catch (error) {
      return { contact: null, error: error as Error };
    }
  },

  // Delete a contact
  async deleteContact(id: string): Promise<{ error: Error | null }> {
    try {
      // Check if contact is editable
      const { data: contact, error: fetchError } = await supabase
        .from('contacts')
        .select('is_editable, profile_picture_url')
        .eq('id', id)
        .single();

      if (fetchError) throw fetchError;

      if (!contact.is_editable) {
        throw new Error('Cannot delete default contacts');
      }

      // Delete profile picture if exists
      if (contact.profile_picture_url) {
        await contactsService.deleteProfilePicture(contact.profile_picture_url);
      }

      const { error } = await supabase
        .from('contacts')
        .delete()
        .eq('id', id);

      if (error) throw error;
      return { error: null };
    } catch (error) {
      return { error: error as Error };
    }
  },

  // Upload profile picture
  async uploadProfilePicture(file: File, contactId?: string, businessId?: string): Promise<string | undefined> {
    try {
      if (!businessId) {
        throw new Error('Business ID is required for file upload');
      }

      // Generate file path
      const timestamp = Date.now();
      const fileExt = file.name.split('.').pop();
      const fileName = contactId ? `${contactId}_${timestamp}.${fileExt}` : `temp_${timestamp}.${fileExt}`;
      const filePath = `${businessId}/${fileName}`;

      // Upload file
      const { error } = await supabase.storage
        .from('contact-pictures')
        .upload(filePath, file, {
          cacheControl: '3600',
          upsert: false,
        });

      if (error) throw error;

      // Get public URL
      const { data: { publicUrl } } = supabase.storage
        .from('contact-pictures')
        .getPublicUrl(filePath);

      return publicUrl;
    } catch (error) {
      console.error('Error uploading profile picture:', error);
      throw error;
    }
  },

  // Delete profile picture from storage
  async deleteProfilePicture(url: string): Promise<{ error: Error | null }> {
    try {
      // Extract file path from URL
      const urlParts = url.split('/');
      const fileName = urlParts.slice(-2).join('/'); // Get business_id/filename

      const { error } = await supabase.storage
        .from('contact-pictures')
        .remove([fileName]);

      if (error) throw error;
      return { error: null };
    } catch (error) {
      console.error('Error deleting profile picture:', error);
      return { error: error as Error };
    }
  },

  // Get contact balance formatted
  getFormattedBalance(balance: number): string {
    return formatCurrency(balance);
  },

  // Update contact balance
  async updateContactBalance(contactId: string, newBalance: number): Promise<{ error: Error | null }> {
    try {
      const { error } = await supabase
        .from('contacts')
        .update({ balance: newBalance })
        .eq('id', contactId);

      if (error) throw error;
      return { error: null };
    } catch (error) {
      return { error: error as Error };
    }
  },

  // Get contact type label
  getContactTypeLabel(type: string): string {
    const labels: Record<string, string> = {
      customer: 'Customer',
      vendor: 'Vendor',
      employee: 'Employee',
      other: 'Other',
    };
    return labels[type] || type;
  },
};